// Utility functions for formatting
import { Home, Map, Calendar, Users, UsersRound } from 'lucide-react';

// ─── Group type config (single source of truth for all pages) ────────────────
// emoji from DB always takes priority; this is the fallback display
export const GROUP_TYPE_CONFIG = {
    household: { icon: Home,     bg: '#16A34A', label: 'Household' },
    trip:      { icon: Map,      bg: '#7C3AED', label: 'Trip'      },
    event:     { icon: Calendar, bg: '#EA580C', label: 'Event'     },
    community: { icon: Users,    bg: '#0D9488', label: 'Community' },
    custom:    { icon: UsersRound, bg: '#4338CA', label: 'Custom'    },
};

export const getGroupTypeConfig = (type) =>
    GROUP_TYPE_CONFIG[type] || GROUP_TYPE_CONFIG.custom;

// ─── Member avatar colours (no red — red reads as error) ─────────────────────
export const MEMBER_AVATAR_COLORS = [
    '#2563EB', // blue
    '#0D9488', // teal
    '#7C3AED', // purple
    '#D97706', // amber
    '#16A34A', // green
    '#4338CA', // indigo
    '#EA580C', // orange
];

// Deterministic colour from name — full string sum avoids collisions
export const getMemberAvatarColor = (name) => {
    const n = (name || '').trim();
    let sum = 0;
    for (let i = 0; i < n.length; i++) sum += n.charCodeAt(i);
    return MEMBER_AVATAR_COLORS[sum % MEMBER_AVATAR_COLORS.length];
};

// Member initials from display name — "Rohan Mohite" → "RM", "Rohan" → "RO"
export const getMemberInitials = (name) => {
    const n = (name || '?').trim();
    const words = n.split(/\s+/);
    return words.length > 1
        ? (words[0][0] + words[1][0]).toUpperCase()
        : n.slice(0, 2).toUpperCase();
};

// Get greeting based on time of day
// 05:00–11:59 → Good morning
// 12:00–16:59 → Good afternoon
// 17:00–04:59 → Good evening (never show "Good night" — fold into evening)
export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
};

// Currency symbol mapping
const CURRENCY_SYMBOLS = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  INR: "₹",
  JPY: "¥",
  AUD: "A$",
  CAD: "C$",
};

export const getCurrencySymbol = (currency) => {
  if (!currency) return "$";
  return CURRENCY_SYMBOLS[currency.toUpperCase()] || currency.toUpperCase() + " ";
};

// Format currency with symbol
export const formatCurrency = (amount, currency) => {
  const symbol = getCurrencySymbol(currency);
  const formattedAmount = Number(amount || 0).toFixed(2);
  return `${symbol}${formattedAmount}`;
};

// Format date for receipt display - "Yesterday", "2 days ago", "17 Apr", or "17 Apr 2024" if different year
export const formatReceiptDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;

  const sameYear = date.getFullYear() === now.getFullYear();
  const monthOptions = sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };

  return date.toLocaleDateString('en-GB', monthOptions);
};

// Always-GBP currency formatter for dashboard totals (per spec)
export const formatGBP = (amount) => {
  const n = Number(amount || 0);
  return `£${n.toFixed(2)}`;
};

// Category pill style
export const getCategoryPill = (category) => {
  const map = {
    "Groceries": { bg: "#F0FDF4", color: "#16A34A" },
    "Dining": { bg: "#FEF3C7", color: "#B45309" },
    "Shopping": { bg: "#EEF2FF", color: "#4338CA" },
    "Transport": { bg: "#EFF6FF", color: "#2563EB" },
    "Entertainment": { bg: "#F5F3FF", color: "#6D28D9" },
    "Luxury": { bg: "#FDF2F8", color: "#9D174D" },
  };
  return map[category] || { bg: "#F1F5F9", color: "#64748B" };
};

// Loyalty card palette (index 0–7). colour_index = card_name.charCodeAt(0) % 8
export const CARD_PALETTE = [
  { from: "#E31837", to: "#9B0E24" }, // 0 Red
  { from: "#00539B", to: "#003368" }, // 1 Blue
  { from: "#00704A", to: "#1E3932" }, // 2 Dark green
  { from: "#512888", to: "#2D1466" }, // 3 Purple
  { from: "#0D9488", to: "#0F766E" }, // 4 Teal
  { from: "#D97706", to: "#B45309" }, // 5 Amber
  { from: "#1A7F37", to: "#14532D" }, // 6 Green
  { from: "#6B1F2A", to: "#3D0A12" }, // 7 Dark red
];

export const getCardGradient = (card) => {
  let idx;
  if (card && Number.isInteger(card.colour_index)) {
    idx = card.colour_index;
  } else {
    const name = card?.card_name || card?.name || '';
    idx = name ? name.charCodeAt(0) % 8 : 0;
  }
  idx = ((idx % 8) + 8) % 8;
  const { from, to } = CARD_PALETTE[idx];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
};

// Full month name and year for summary card e.g. "April 2026"
export const formatMonthYear = (date = new Date()) => {
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

// Merchant avatar: first 2 letters (uppercase) + color based on first letter
export const getMerchantAvatar = (merchantName) => {
  const name = (merchantName || "?").trim();
  const initials = name.slice(0, 2).toUpperCase();
  const firstChar = name.charAt(0).toUpperCase();

  let bgColor = "#2D5BFF"; // default blue

  if (/[A-E]/.test(firstChar)) bgColor = "#0D9488"; // teal
  else if (/[F-J]/.test(firstChar)) bgColor = "#7C3AED"; // purple
  else if (/[K-P]/.test(firstChar)) bgColor = "#16A34A"; // green
  else if (/[Q-Z]/.test(firstChar)) bgColor = "#D97706"; // amber

  return { initials, bgColor, textColor: "#FFFFFF" };
};

// Compute monthly summary from receipts array
// Uses transaction_date only (per spec)
export const computeMonthlySummary = (receipts = []) => {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();

  const monthReceipts = receipts.filter((r) => {
    if (!r.transaction_date) return false;
    const d = new Date(r.transaction_date);
    if (isNaN(d.getTime())) return false;
    return d.getMonth() === month && d.getFullYear() === year;
  });

  const total = monthReceipts.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  // All amounts in GBP (£) per spec.
  return {
    total,
    count: monthReceipts.length,
    currency: "GBP",
    monthLabel: formatMonthYear(now),
  };
};

// Get category icon and color (kept for backward compat)
export const getCategoryStyle = (category) => {
  const styles = {
    "Food & Dining": { icon: "utensils", bgColor: "#DBEAFE", iconColor: "#1D4ED8" },
    "Shopping": { icon: "shopping-bag", bgColor: "#FFE4E6", iconColor: "#9F1239" },
    "Transportation": { icon: "fuel", bgColor: "#DBEAFE", iconColor: "#1D4ED8" },
    "Entertainment": { icon: "ticket", bgColor: "#F3E8FF", iconColor: "#7C3AED" },
    "Bills & Utilities": { icon: "file-text", bgColor: "#DCFCE7", iconColor: "#16A34A" },
    "Healthcare": { icon: "heart", bgColor: "#FEE2E2", iconColor: "#DC2626" },
    "Travel": { icon: "plane", bgColor: "#E0F2FE", iconColor: "#0284C7" },
    "Other": { icon: "receipt", bgColor: "#F3F4F6", iconColor: "#6B7280" }
  };

  return styles[category] || styles["Other"];
};